import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';
import CreateTeamWizard from './CreateTeamWizard';
import { api } from '../../../services/api';
import { getUserUsageV2, type UserUsageV2 } from '../../../services/userUsage';
import { API_BASE_URL } from '../../../utils/constants';

// Types
interface Team {
  id: string;
  name: string;
  description?: string;
  color: string;
  assignment_strategy: string;
  max_concurrent_chats: number;
  auto_assign_enabled: boolean;
  timezone: string;
  working_hours: Record<string, any>;
  created_at: string;
  is_active: boolean;
  member_count: number;
  online_agents: number;
  assigned_chatbots: number;
}

interface Chatbot {
  id: string;
  name: string;
  description: string;
}

interface TeamCardProps {
  team: Team;
  onClick: () => void;
}

const TeamCard: React.FC<TeamCardProps> = ({ team, onClick }) => {
  const getStatusColor = (onlineAgents: number, totalAgents: number) => {
    if (onlineAgents === 0) return 'bg-red-500';
    if (onlineAgents === totalAgents) return 'bg-green-500';
    return 'bg-yellow-500';
  };

  const getStatusText = (onlineAgents: number, totalAgents: number) => {
    if (onlineAgents === 0) return 'Offline';
    if (onlineAgents === totalAgents) return 'All Online';
    return 'Partial Coverage';
  };

  return (
    <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={onClick}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center mb-2">
              <div
                className="w-3 h-3 rounded-full mr-2"
                style={{ backgroundColor: team.color }}
              />
              <h3 className="text-lg font-semibold text-gray-900">{team.name}</h3>
            </div>
            {team.description && (
              <p className="text-sm text-gray-600 mb-3">{team.description}</p>
            )}
          </div>
          <div className={`w-3 h-3 rounded-full ${getStatusColor(team.online_agents, team.member_count)}`} />
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Team Members</span>
            <div className="flex items-center">
              <span className="font-medium text-gray-900">{team.online_agents}</span>
              <span className="text-gray-400 mx-1">/</span>
              <span className="text-gray-600">{team.member_count}</span>
              <span className="ml-2 text-xs text-gray-500">online</span>
            </div>
          </div>

          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Assigned Chatbots</span>
            <span className="font-medium text-gray-900">{team.assigned_chatbots}</span>
          </div>

          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Assignment</span>
            <Badge variant="outline">
              {team.assignment_strategy.replace('_', ' ')}
            </Badge>
          </div>

          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Status</span>
            <Badge
              variant={team.online_agents > 0 ? "default" : "secondary"}
            >
              {getStatusText(team.online_agents, team.member_count)}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const CreateTeamCard: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <Card className="cursor-pointer hover:shadow-lg transition-shadow border-dashed border-2" onClick={onClick}>
    <CardContent className="p-6 flex flex-col items-center justify-center h-full min-h-[200px]">
      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Create New Team</h3>
      <p className="text-sm text-gray-600 text-center">
        Organize your agents into teams for better conversation management
      </p>
    </CardContent>
  </Card>
);

const TeamsDashboard: React.FC = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [chatbots, setChatbots] = useState<Chatbot[]>([]);
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [showEditWizard, setShowEditWizard] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [usageV2, setUsageV2] = useState<UserUsageV2 | null>(null);

  // Fetch teams and chatbots
  useEffect(() => {
    fetchTeams();
    fetchChatbots();
    getUserUsageV2().then(setUsageV2).catch(() => null);
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await api.get('/teams/');
      setTeams(response.data);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChatbots = async () => {
    try {
      const response = await api.get('/chatbots');
      setChatbots(response.data.chatbots || []);
    } catch (error) {
      console.error('Error fetching chatbots:', error);
    }
  };

  const handleCreateTeam = async (teamData: any) => {
    try {
      // Create the team first
      const teamResponse = await api.post('/teams/', {
        name: teamData.name,
        description: teamData.description,
        color: teamData.color,
        assignment_strategy: teamData.assignment_strategy,
        max_concurrent_chats: teamData.max_concurrent_chats,
        auto_assign_enabled: teamData.auto_assign_enabled,
        timezone: teamData.timezone,
        working_hours: teamData.working_hours
      });

      const newTeam = teamResponse.data;

      // Send invitations
      if (teamData.agent_invites && teamData.agent_invites.length > 0) {
        const token = localStorage.getItem('token');
        for (const invite of teamData.agent_invites) {
          await fetch(`${API_BASE_URL}/teams/${newTeam.id}/invite`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(invite)
          });
        }
      }

      // Assign chatbots
      if (teamData.chatbot_assignments && teamData.chatbot_assignments.length > 0) {
        for (const assignment of teamData.chatbot_assignments) {
          await fetch(`${API_BASE_URL}/teams/${newTeam.id}/assign-chatbot`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              chatbot_id: assignment.chatbot_id,
              assignment_priority: assignment.priority
            })
          });
        }
      }

      // Refresh teams list
      await fetchTeams();
      setShowCreateWizard(false);

      // Show success message
      alert('Team created successfully!');
    } catch (error) {
      console.error('Error creating team:', error);
      alert('Failed to create team. Please try again.');
    }
  };

  const handleTeamClick = async (team: Team) => {
    try {
      // Fetch existing chatbot assignments for this team
      const assignmentsResponse = await api.get(`/teams/${team.id}/chatbots`);
      const assignments = assignmentsResponse.data || [];

      // Convert to the format expected by the wizard
      const chatbotAssignments = assignments.map((assignment: any) => ({
        chatbot_id: assignment.chatbot_id,
        priority: assignment.assignment_priority
      }));

      // Add assignments to team data
      const teamWithAssignments = {
        ...team,
        chatbot_assignments: chatbotAssignments
      };

      setSelectedTeam(teamWithAssignments);
      setShowEditWizard(true);
      console.log('Editing team:', teamWithAssignments);
      console.log('Available chatbots:', chatbots);
    } catch (error) {
      console.error('Error fetching team assignments:', error);
      // Fallback to editing without assignments
      setSelectedTeam(team);
      setShowEditWizard(true);
    }
  };

  const handleEditTeam = async (teamData: any) => {
    try {
      if (!selectedTeam) return;

      // Update the team
      const teamResponse = await api.put(`/teams/${selectedTeam.id}`, {
        name: teamData.name,
        description: teamData.description,
        color: teamData.color,
        assignment_strategy: teamData.assignment_strategy,
        max_concurrent_chats: teamData.max_concurrent_chats,
        auto_assign_enabled: teamData.auto_assign_enabled,
        timezone: teamData.timezone,
        working_hours: teamData.working_hours
      });

      // Refresh teams list
      await fetchTeams();
      setShowEditWizard(false);
      setSelectedTeam(null);

      // Show success message
      alert('Team updated successfully!');
    } catch (error) {
      console.error('Error updating team:', error);
      alert('Failed to update team. Please try again.');
    }
  };

  if (showCreateWizard) {
    return (
      <CreateTeamWizard
        onComplete={handleCreateTeam}
        onCancel={() => setShowCreateWizard(false)}
        chatbots={chatbots || []}
      />
    );
  }

  if (showEditWizard) {
    return (
      <CreateTeamWizard
        onComplete={handleEditTeam}
        onCancel={() => {
          setShowEditWizard(false);
          setSelectedTeam(null);
        }}
        chatbots={chatbots || []}
        initialData={selectedTeam}
        isEditing={true}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading teams...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Teams</h1>
          <p className="text-gray-600">
            Organize your agents into teams for better conversation management
          </p>
        </div>
        <Button
          onClick={() => setShowCreateWizard(true)}
          disabled={!!(usageV2 && usageV2.teams.limit !== -1 && usageV2.teams.used >= usageV2.teams.limit)}
          title={usageV2 && usageV2.teams.limit !== -1 && usageV2.teams.used >= usageV2.teams.limit ? 'Teams limit reached. Upgrade plan to add more.' : undefined}
        >
          Create Team
        </Button>
      </div>

      {/* Teams Overview */}
      {teams.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{teams.length}</div>
                <div className="text-sm text-gray-600">Total Teams</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {teams.reduce((sum, team) => sum + team.online_agents, 0)}
                </div>
                <div className="text-sm text-gray-600">Agents Online</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {teams.reduce((sum, team) => sum + team.member_count, 0)}
                </div>
                <div className="text-sm text-gray-600">Total Agents</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {teams.reduce((sum, team) => sum + team.assigned_chatbots, 0)}
                </div>
                <div className="text-sm text-gray-600">Assigned Chatbots</div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Teams Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {teams.map(team => (
          <TeamCard
            key={team.id}
            team={team}
            onClick={() => handleTeamClick(team)}
          />
        ))}

        <CreateTeamCard onClick={() => setShowCreateWizard(true)} />
      </div>

      {/* Empty State */}
      {teams.length === 0 && (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No teams yet</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Create your first team to organize agents and manage conversations more effectively.
            Teams help distribute workload and provide better customer support.
          </p>
          <Button onClick={() => setShowCreateWizard(true)}>
            Create Your First Team
          </Button>
        </div>
      )}

      {/* Quick Stats */}
      {teams.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Team Performance Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Most Active Team</h4>
                <div className="text-sm text-gray-600">
                  {teams.length > 0 ? (
                    (() => {
                      const mostActive = teams.reduce((prev, current) =>
                        prev.online_agents > current.online_agents ? prev : current
                      );
                      return (
                        <div className="flex items-center">
                          <div
                            className="w-2 h-2 rounded-full mr-2"
                            style={{ backgroundColor: mostActive.color }}
                          />
                          <span>{mostActive.name}</span>
                          <span className="ml-2 text-gray-500">
                            ({mostActive.online_agents} online)
                          </span>
                        </div>
                      );
                    })()
                  ) : (
                    'No active teams'
                  )}
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Coverage Status</h4>
                <div className="text-sm text-gray-600">
                  {(() => {
                    const onlineTeams = teams.filter(t => t.online_agents > 0).length;
                    const totalTeams = teams.length;
                    const percentage = totalTeams > 0 ? Math.round((onlineTeams / totalTeams) * 100) : 0;

                    return (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span>{onlineTeams}/{totalTeams} teams online</span>
                          <span>{percentage}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-green-600 h-2 rounded-full"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Avg Team Size</h4>
                <div className="text-sm text-gray-600">
                  {teams.length > 0 ? (
                    Math.round(teams.reduce((sum, team) => sum + team.member_count, 0) / teams.length)
                  ) : (
                    0
                  )} agents per team
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TeamsDashboard;
